import axios from "axios";
import React, { useContext, useEffect, useRef, useState } from "react";
import DOMPurify from "dompurify";
import { AuthContext } from "../context/AuthContext";
import MenuBookIcon from '@mui/icons-material/MenuBook';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from "@mui/icons-material/AddCircle";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ENDPOINT from "../data/ENDPOINT";



export default function ClassNotes({ currentVideoTitle, batchId, courseId, videoId }) {
  const { user } = useContext(AuthContext);
  const [newNote, setNewNote] = useState("");
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [allNotes, setAllNotes] = useState([]);
  const [error, setError] = useState(null);
  const [editNoteId, setEditNoteId] = useState(null);
  const [editNoteContent, setEditNoteContent] = useState("");
  const editorRef = useRef(null);
  const editRef = useRef(null);



  const getNotes = async () => {
    const data = new FormData();
    data.append("session", user.SessionId);
    data.append("batch_id", batchId);
    data.append("course_id", courseId);
    data.append("video_id", videoId);
    try {
      const res = await axios.post(`${ENDPOINT}get-class-notes.php`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data.status === "success") {
        setAllNotes(res.data.data);
        setError(null);
      } else {
        setError("Failed to fetch notes.");
      }
    } catch (err) {
      setError("An error occurred while fetching notes.");
    }
  };

  const addNotes = async () => {
    const plainText = newNote.replace(/<[^>]+>/g, "").trim();
    if (!plainText) return;
    const data = new FormData();
    data.append("notes", newNote);
    data.append("session", user.SessionId);
    data.append("batch_id", batchId);
    data.append("course_id", courseId);
    data.append("video_id", videoId);
    try {
      const response = await axios.post(`${ENDPOINT}student-add-class-notes.php`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response.data.status === "success") {
        setAllNotes((prev) => [
          ...prev,
          { notes_id: response.data.notes_id, notes: newNote, create_date: new Date().toISOString(), videoTitle: currentVideoTitle },
        ]);
        setNewNote("");
        setError(null);
        getNotes()
      } else {
        setError("Failed to add note.");
      }
    } catch (error) {
      setError("Error adding note.");
    }
  };

  const onDeleteNote = async (id) => {
    const data = new FormData();
    data.append("notes_id", id);
    data.append("session", user.SessionId);
    data.append("video_id", videoId);
    try {
      const res = await axios.post(`${ENDPOINT}delete-student-notes.php`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data.status === "success") {
        setAllNotes((prev) => prev.filter((note) => note.notes_id !== id));
        setError(null);
      } else {
        setError("Failed to delete note.");
      }
    } catch (err) {
      setError("Error deleting note.");
    }
  };

  // Edit note API
  const updateNote = async (noteId) => {
    const plainText = editNoteContent.replace(/<[^>]+>/g, "").trim();
    if (!plainText) return;
    const data = new FormData();
    data.append("notes_id", noteId);
    data.append("video_id", videoId);
    data.append("notes", editNoteContent);
    data.append("session", user.SessionId);
    try {
      const res = await axios.post(`${ENDPOINT}update-student-class-notes.php`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data.status === "success") {
        setAllNotes((prev) =>
          prev.map((note) =>
            note.notes_id === noteId ? { ...note, notes: editNoteContent } : note
          )
        );
        setEditNoteId(null);
        setEditNoteContent("");
        setError(null);
      } else {
        setError("Failed to update note.");
      }
    } catch (err) {
      setError("Error updating note.");
    }
  };

  // Helper to get plain text length
  const getPlainTextLength = () => {
    const div = document.createElement("div");
    div.innerHTML = editorRef.current && editorRef.current.innerHTML || "";
    return div.textContent.length;
  };

  // Handle input with character limit
  const handleInput = (e) => {
    const text = e.currentTarget.innerText;
    if (text.length > 1000) {
      // Prevent further input
      e.currentTarget.innerText = text.slice(0, 1000);
      setNewNote(e.currentTarget.innerHTML);
      // Move cursor to end
      document.execCommand("selectAll", false, null);
      document.getSelection().collapseToEnd();
      return;
    }
    setNewNote(e.currentTarget.innerHTML);
  };

  // Focus editor and run command
  const handleFormat = (cmd) => {
    editorRef.current.focus();
    document.execCommand(cmd);
    setNewNote(editorRef.current.innerHTML);
  };

  // Handle edit input with character limit
  const handleEditInput = (e) => {
    const text = e.currentTarget.innerText;
    if (text.length > 1000) {
      // Prevent further input
      e.currentTarget.innerText = text.slice(0, 1000);
      setEditNoteContent(e.currentTarget.innerHTML);
      // Move cursor to end
      document.execCommand("selectAll", false, null);
      document.getSelection().collapseToEnd();
      return;
    }
    setEditNoteContent(e.currentTarget.innerHTML);
  };

  // Focus editor and run command for edit
  const handleEditFormat = (cmd) => {
    editRef.current.focus();
    document.execCommand(cmd);
    setEditNoteContent(editRef.current.innerHTML);
  };

  // Get plain text length for edit
  const getEditPlainTextLength = () => {
    const div = document.createElement("div");
    div.innerHTML = editNoteContent;
    return div.textContent.length;
  };

  useEffect(() => {
    getNotes();
  }, [currentVideoTitle, videoId]);

  return (
    <div className="card shadow border-0 p-4 notes-card" style={{ backgroundColor: "#feebd2" }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center">
          <div
            className="rounded-circle d-flex align-items-center justify-content-center me-3"
            style={{ width: "42px", height: "42px", background: "linear-gradient(135deg, #111211, #2d2e2e)", color: "white", fontSize: "18px" }}
          >
            <MenuBookIcon></MenuBookIcon>
          </div>
          <div>
            <h5 className="mb-0 fw-bold text-dark p-0">Class Notes</h5>
            <small className="text-secondary">For: {currentVideoTitle}</small>
          </div>
        </div>
        <button
          size="small"
          onClick={() => setIsMobileOpen((prev) => !prev)}
          aria-label={isMobileOpen ? "Hide notes section" : "Show notes section"}
          className="d-md-none btn p-1 rounded"
          onMouseEnter={(e) => {
            e.currentTarget.style.background =
              "linear-gradient(135deg, #ff7a3c, #ff5a1f)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background =
              "linear-gradient(135deg, #f58752, #ff6a2f)";
          }}
          onMouseDown={(e) => {
            e.currentTarget.style.background =
              "linear-gradient(135deg, #ff6a2f, #e8571f)";
          }}
          onMouseUp={(e) => {
            e.currentTarget.style.background =
              "linear-gradient(135deg, #ff7a3c, #ff5a1f)";
          }}
          onFocus={(e) => {
            e.currentTarget.style.boxShadow =
              "0 0 0 0.2rem rgba(245, 135, 82, 0.45)";
          }}
          onBlur={(e) => {
            e.currentTarget.style.boxShadow =
              "0 4px 10px rgba(245, 135, 82, 0.35)";
          }}
          style={{
            background: "linear-gradient(135deg, #f58752, #ff6a2f)",
            color: "#fff",
            border: "none",
            boxShadow: "0 4px 10px rgba(245, 135, 82, 0.35)",
            transition: "all 0.2s ease",
          }}
        >
          {isMobileOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </button>

      </div>

      <div className={`collapse ${isMobileOpen ? "show" : ""} d-md-block`}>
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
        <form onSubmit={(e) => { e.preventDefault(); addNotes(); }} className="mb-4" style={{ position: "relative" }}>
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            className="form-control notes-editor shadow-sm"
            style={{ minHeight: "80px", maxHeight: "200px", overflowY: "auto" }}
            aria-label="Write your class notes here"
            onInput={handleInput}
            // Only set initial value if empty
            dangerouslySetInnerHTML={newNote ? undefined : { __html: newNote }}
          />
          <div className="d-flex flex-wrap align-items-center my-3 gap-2">
            <button type="button" className="btn btn btn-outline-secondary" title="Bold" onClick={() => handleFormat("bold")}>
              <FormatBoldIcon

                style={{ fontSize: "15px" }}>

              </FormatBoldIcon>
            </button>
            <button type="button" className="btn btn btn-outline-secondary" title="Italic" onClick={() => handleFormat("italic")}>
              <FormatItalicIcon

                style={{ fontSize: "15px" }}></FormatItalicIcon>
            </button>
            <button type="button" className="btn btn btn-outline-secondary" title="Underline" onClick={() => handleFormat("underline")}>
              <FormatUnderlinedIcon
                style={{ fontSize: "15px" }}></FormatUnderlinedIcon>
            </button>
            <button type="button" className="btn btn btn-outline-secondary" title="Bullet List" onClick={() => handleFormat("insertUnorderedList")}>
              <FormatListBulletedIcon
                style={{ fontSize: "15px" }}></FormatListBulletedIcon>
            </button>
            <button type="button" className="btn btn btn-outline-secondary" title="Numbered List" onClick={() => handleFormat("insertOrderedList")}>
              <FormatListNumberedIcon
                style={{ fontSize: "15px" }}></FormatListNumberedIcon>
            </button>
            <span className=" text-muted small ms-1">{getPlainTextLength()}/1000 characters</span>
            <div className="ms-auto">
              <button className="btn bg-siliconMinds-orng text-light border-none" disabled={getPlainTextLength() === 0} type="submit">
                <AddCircleIcon sx={{ mr: 1 }} />Add Note
              </button>
            </div>
          </div>

        </form>
        {Array.isArray(allNotes) && allNotes.length === 0 ? (
          <div className="text-center py-3 text-secondary">
            <MenuBookIcon></MenuBookIcon>
            <p className="mb-0 fw-semibold">No notes yet for this video.</p>
            <small>Start taking notes to remember key points!</small>
          </div>
        ) : (
          <div style={{ maxHeight: "130px", overflowY: "auto" }}>
            {allNotes.map((note) => (
              <div
                key={note.notes_id}
                className="mb-3 p-3 rounded shadow-sm"
                style={{
                  background: "linear-gradient(135deg, #fff7f2 0%, #eaecef 60%, #eef1f5 100%)",
                  borderLeft: "4px solid #f58752",
                  boxShadow: "0 6px 14px rgba(0,0,0,0.08)"
                }}
              >
                <div className="d-flex justify-content-between align-items-start">
                  {editNoteId === note.notes_id ? (
                    <div className="flex-grow-1 w-100">
                      <div
                        ref={editRef}
                        contentEditable
                        suppressContentEditableWarning
                        className="form-control mb-2 border-success-subtle shadow-sm"
                        style={{ minHeight: "80px", maxHeight: "200px", overflowY: "auto", background: "#fff" }}
                        aria-label="Edit your note"
                        onInput={handleEditInput}
                      // DO NOT use dangerouslySetInnerHTML here!
                      />
                      <div className="d-flex flex-wrap align-items-center my-2 gap-2">
                        <button type="button" className="btn btn btn-outline-secondary" title="Bold" onClick={() => handleEditFormat("bold")}>
                          <FormatBoldIcon
                            style={{ fontSize: "14px" }}></FormatBoldIcon>
                        </button>
                        <button type="button" className="btn btn btn-outline-secondary" title="Italic" onClick={() => handleEditFormat("italic")}>
                          <FormatItalicIcon
                            style={{ fontSize: "14px" }}></FormatItalicIcon>
                        </button>
                        <button type="button" className="btn btn btn-outline-secondary" title="Underline" onClick={() => handleEditFormat("underline")}>
                          <FormatUnderlinedIcon
                            style={{ fontSize: "14px" }}></FormatUnderlinedIcon>
                        </button>
                        <button type="button" className="btn btn btn-outline-secondary" title="Bullet List" onClick={() => handleEditFormat("insertUnorderedList")}>
                          <FormatListBulletedIcon
                            style={{ fontSize: "14px" }}></FormatListBulletedIcon>
                        </button>
                        <button type="button" className="btn btn btn-outline-secondary" title="Numbered List" onClick={() => handleEditFormat("insertOrderedList")}>
                          <FormatListNumberedIcon
                            style={{ fontSize: "14px" }}></FormatListNumberedIcon>
                        </button>
                        <span className="text-muted small ms-2">{getEditPlainTextLength()}/1000 characters</span>
                        <div className="ms-auto d-flex gap-2">
                          <button
                            className="btn btn btn-success"
                            onClick={() => updateNote(note.notes_id)}
                            disabled={getEditPlainTextLength() === 0}
                          >
                            Save
                          </button>
                          <button
                            className="btn btn btn-secondary"
                            onClick={() => {
                              setEditNoteId(null);
                              setEditNoteContent("");
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="mb-1 text-dark flex-grow-1" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(note.notes) }} />
                  )}
                  <div className="ms-2 d-flex flex-column align-items-end">
                    <small className="text-muted">{note.create_date}</small>
                    <div className="d-flex gap-1 mt-1">
                      <button
                        className="btn btn text-primary p-0"
                        title="Edit Note"
                        onClick={() => {
                          setEditNoteId(note.notes_id);
                          setEditNoteContent(note.notes);
                          setTimeout(() => {
                            if (editRef.current) {
                              editRef.current.innerHTML = note.notes; // Set initial HTML directly
                              editRef.current.focus();
                              // Move cursor to end
                              const range = document.createRange();
                              range.selectNodeContents(editRef.current);
                              range.collapse(false);
                              const sel = window.getSelection();
                              sel.removeAllRanges();
                              sel.addRange(range);
                            }
                          }, 0);
                        }}
                      >
                        <span className="visually-hidden">Edit Note</span>
                        <EditIcon></EditIcon>
                      </button>
                      <button
                        className="btn btn text-danger p-0"
                        onClick={() => onDeleteNote(note.notes_id)}
                        title="Delete Note"
                      >
                        <span className="visually-hidden">Delete Note</span>
                        <DeleteIcon />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}